import type { TotpEnrollment } from "@kopi/contracts";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@kopi/ui/components/card";
import { Input } from "@kopi/ui/components/input";
import { Label } from "@kopi/ui/components/label";
import { ResponsiveDialog } from "@kopi/ui/components/responsive-dialog";
import { toast } from "@kopi/ui/components/sonner";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, useId, useState } from "react";
import { renderSVG } from "uqr";
import { Page, PageHeader } from "@/components/page-header";
import { security } from "@/i18n/messages/security";
import { useMessages } from "@/i18n/provider";
import { ApiRequestError, api, unwrap } from "@/lib/api";
import { mfaStatusQuery } from "@/lib/queries";

export const Route = createFileRoute("/_authed/security")({
  staticData: {
    section: "preferences",
    parent: { label: "nav.settings", to: "/settings" },
    crumb: "page.security",
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(mfaStatusQuery),
  component: SecurityPage,
});

function SecurityPage() {
  const { t } = useMessages(security);
  const { data } = useSuspenseQuery(mfaStatusQuery);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>();

  return (
    <Page width="narrow">
      <PageHeader title={t("page.security")} subtitle={t("security.subtitle")} />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {t("security.twoStep")}
            {data.totpEnabled ? (
              <Badge variant="success">{t("security.on")}</Badge>
            ) : (
              <Badge variant="outline">{t("security.off")}</Badge>
            )}
          </CardTitle>
          <CardDescription>{t(data.totpEnabled ? "security.onHint" : "security.offHint")}</CardDescription>
        </CardHeader>
        <CardContent>
          {recoveryCodes ? (
            <RecoveryCodesPanel codes={recoveryCodes} onDone={() => setRecoveryCodes(undefined)} />
          ) : data.totpEnabled ? (
            <DisableFlow />
          ) : (
            <EnrolFlow onEnabled={setRecoveryCodes} />
          )}
        </CardContent>
      </Card>
    </Page>
  );
}

function EnrolFlow(props: { onEnabled: (codes: string[]) => void }) {
  const { t } = useMessages(security);
  const queryClient = useQueryClient();
  const [enrollment, setEnrollment] = useState<TotpEnrollment>();
  const [code, setCode] = useState("");
  const codeId = useId();

  const start = useMutation({
    mutationFn: async () => unwrap(await api.v1.me.mfa.totp.post()),
    onSuccess: setEnrollment,
    onError: () => toast.error(t("security.startFailed")),
  });
  const confirm = useMutation({
    mutationFn: async () => unwrap(await api.v1.me.mfa.totp.confirm.post({ code })),
    onSuccess: async (result) => {
      setEnrollment(undefined);
      props.onEnabled(result.recoveryCodes);
      await queryClient.invalidateQueries({ queryKey: mfaStatusQuery.queryKey });
      toast.success(t("security.enabled"));
    },
    onError: () => setCode(""),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!confirm.isPending && code.length === 6) confirm.mutate();
  }

  // The QR is generated locally from our own otpauth URI and shown as an image, never as markup.
  const qr = enrollment && `data:image/svg+xml;utf8,${encodeURIComponent(renderSVG(enrollment.otpauthUri))}`;
  return (
    <>
      <Button type="button" onClick={() => start.mutate()} disabled={start.isPending}>
        {t(start.isPending ? "security.preparing" : "security.enable")}
      </Button>
      <ResponsiveDialog
        open={enrollment !== undefined}
        onOpenChange={(open) => {
          if (open) return;
          setEnrollment(undefined);
          setCode("");
          confirm.reset();
        }}
        title={t("security.enable")}
        description={t("security.enableHint")}
        dismissible={!confirm.isPending}
        footer={
          <Button type="submit" form="totp-confirm" disabled={confirm.isPending || code.length !== 6}>
            {t(confirm.isPending ? "security.checking" : "security.confirm")}
          </Button>
        }
      >
        <form id="totp-confirm" className="flex flex-col gap-3" onSubmit={submit} noValidate>
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            <li>{t("security.stepScan")}</li>
            <li>{t("security.stepCode")}</li>
          </ol>
          {qr && <img src={qr} alt={t("security.qr")} className="mx-auto size-48 rounded bg-scannable p-2" />}
          <p className="break-all text-center font-mono text-muted-foreground text-xs">
            {enrollment?.secret}
          </p>
          <Label htmlFor={codeId}>{t("security.code")}</Label>
          <Input
            id={codeId}
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            className="text-center font-mono text-lg tracking-widest"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          {confirm.error && (
            <p role="alert" className="text-destructive text-sm">
              {t("security.wrongCodeClock")}
            </p>
          )}
        </form>
      </ResponsiveDialog>
    </>
  );
}

function RecoveryCodesPanel(props: { codes: string[]; onDone: () => void }) {
  const { t } = useMessages(security);
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm">{t("security.recoveryHint")}</p>
      <ul className="grid grid-cols-2 gap-2 rounded-lg bg-muted p-3 font-mono text-sm">
        {props.codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <Button
        type="button"
        variant="outline"
        onClick={async () => {
          await navigator.clipboard.writeText(props.codes.join("\n"));
          setCopied(true);
        }}
      >
        {t(copied ? "security.copied" : "security.copy")}
      </Button>
      <Button type="button" onClick={props.onDone}>
        {t("security.saved")}
      </Button>
    </div>
  );
}

function DisableFlow() {
  const { t } = useMessages(security);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const codeId = useId();

  const disable = useMutation({
    mutationFn: async () => unwrap(await api.v1.me.mfa.totp.delete({ code })),
    onSuccess: async () => {
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: mfaStatusQuery.queryKey });
      toast.success(t("security.disabled"));
    },
    onError: () => setCode(""),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!disable.isPending && code.length === 6) disable.mutate();
  }

  const error = disable.error;
  return (
    <>
      <Button type="button" variant="destructive" onClick={() => setOpen(true)}>
        {t("security.disable")}
      </Button>
      <ResponsiveDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setCode("");
            disable.reset();
          }
        }}
        title={t("security.disableTitle")}
        description={t("security.disableHint")}
        dismissible={!disable.isPending}
        footer={
          <Button
            type="submit"
            form="totp-disable"
            variant="destructive"
            disabled={disable.isPending || code.length !== 6}
          >
            {t("common.deactivate")}
          </Button>
        }
      >
        <form id="totp-disable" className="flex flex-col gap-3" onSubmit={submit} noValidate>
          <Label htmlFor={codeId}>{t("security.code")}</Label>
          <Input
            id={codeId}
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            className="text-center font-mono text-lg tracking-widest"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          {error && (
            <p role="alert" className="text-destructive text-sm">
              {t(
                error instanceof ApiRequestError && error.status === 429
                  ? "security.codeLocked"
                  : "security.wrongCode",
              )}
            </p>
          )}
        </form>
      </ResponsiveDialog>
    </>
  );
}
